import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import useFetchObjectsWithConfig from "../../../../api/useFetchObjectsWithConfig";
import { useTranslation } from "react-i18next";

const DEFAULT_LAST_DAYS = 10;
const DEFAULT_LIMIT_USERS = 10;

// For export: pull large pages to reduce round trips (must be <= backend MAX_PAGE_SIZE)
const EXPORT_PAGE_SIZE = 500;

const AttendanceReport2 = ({ deviceConfig: propDeviceConfig }) => {
  const { t } = useTranslation();
  const { user: userStorage } = useAuth();
  const token = userStorage?.token;

  // Calculate attendance status based on timing rules
  const calculateAttendanceStatus = (time, date) => {
    if (!propDeviceConfig?.timingRules || !time) {
      return "Unknown"; // fallback if no timing rules or time
    }

    const timingRules = propDeviceConfig.timingRules;
    const attendanceTime = new Date(`${date} ${time}`);
    const presentTime = new Date(`${date} ${timingRules.presentTime}`);
    const lateTime = new Date(`${date} ${timingRules.lateTime}`);
    const checkoutTime = new Date(`${date} ${timingRules.checkoutTime}`);
    const earlyLeaveTime = new Date(`${date} ${timingRules.earlyLeaveTime}`);

    // Check if it's a check-in (morning) or check-out (evening)
    const isMorning = attendanceTime.getHours() < 12;

    if (isMorning) {
      // Check-in logic
      if (attendanceTime <= presentTime) {
        return "Present";
      } else if (attendanceTime <= lateTime) {
        return "Present"; // Still present but close to late
      } else {
        return "Late";
      }
    } else {
      // Check-out logic
      if (attendanceTime >= checkoutTime) {
        return "Present";
      } else if (attendanceTime >= earlyLeaveTime) {
        return "Present"; // Still present but close to early
      } else {
        return "Early Leave";
      }
    }
  };

  const MonthOptions = [
    { id: "", value: t("hr.allMonths") },
    { id: 1, value: "January" },
    { id: 2, value: "February" },
    { id: 3, value: "March" },
    { id: 4, value: "April" },
    { id: 5, value: "May" },
    { id: 6, value: "June" },
    { id: 7, value: "July" },
    { id: 8, value: "August" },
    { id: 9, value: "September" },
    { id: 10, value: "October" },
    { id: 11, value: "November" },
    { id: 12, value: "December" },
  ];

  const currentYear = new Date().getFullYear();
  const startYear = 2023;
  const Years = [
    { id: "", value: t("hr.allYears") },
    ...Array.from({ length: currentYear - startYear + 1 }, (_, index) => ({
      id: startYear + index,
      value: startYear + index,
    })),
  ];

  // Filters with optimized defaults
  const [user, setUser] = useState("");
  const [year, setYear] = useState(currentYear); // Default to current year
  const [month, setMonth] = useState(new Date().getMonth() + 1); // Default to current month
  const [startDate, setStartDate] = useState(""); // YYYY-MM-DD
  const [endDate, setEndDate] = useState(""); // YYYY-MM-DD
  const [forceFresh, setForceFresh] = useState(false);

  // Server pagination (controlled)
  const [page, setPage] = useState(0); // zero-based for DataGrid
  const [pageSize, setPageSize] = useState(50);
  const [rowCount, setRowCount] = useState(0);

  // Data
  const [userAttendanceData, setUserAttendanceData] = useState([]);
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState(null);

  // Export state
  const [exporting, setExporting] = useState(false);

  // Device configuration state (use prop or default)
  const [deviceConfig, setDeviceConfig] = useState(
    propDeviceConfig || {
      ip: "103.213.107.222",
      port: 4370,
      timeout: 30,
      password: 0,
    }
  );

  // Update device config when prop changes
  useEffect(() => {
    if (propDeviceConfig) {
      setDeviceConfig(propDeviceConfig);
    }
  }, [propDeviceConfig]);

  // Build query string for the main table request
  const queryString = useMemo(() => {
    const p = new URLSearchParams();

    if (user) p.append("user_id", user);
    if (year && year !== "") p.append("year", year);
    if (month && month !== "") p.append("month", month);
    if (startDate) p.append("start_date", startDate);
    if (endDate) p.append("end_date", endDate);

    // server pagination (API is 1-based)
    p.append("page", String(page + 1));
    p.append("page_size", String(pageSize));

    // Use fast default only if no specific filters are applied
    // With optimized defaults (user + year + month), we don't need fast subset
    if (!user && !startDate && !endDate) {
      p.append("last_days", String(DEFAULT_LAST_DAYS));
      p.append("limit_users", String(DEFAULT_LIMIT_USERS));
    }
    if (forceFresh) p.append("fresh", "true");

    return p.toString();
  }, [user, year, month, startDate, endDate, page, pageSize, forceFresh]);

  // Users with device configuration (fallback to default if custom fails)
  const {
    data: usersData,
    isLoading: isLoadingUsers,
    refetch: refetchUsers,
  } = useFetchObjectsWithConfig(
    ["attendance-users", JSON.stringify(deviceConfig)],
    "attendance-config/get_users_with_config/",
    token,
    deviceConfig
  );

  // Fallback to default users endpoint if custom config fails
  const { data: fallbackUsersData, refetch: refetchFallbackUsers } =
    useFetchObjects(["attendance-users-fallback"], "attendance-users/", token);

  useEffect(() => {
    // Use custom config data if available, otherwise fallback to default
    const dataToUse = usersData || fallbackUsersData;

    if (dataToUse) {
      const formatted = Object.entries(dataToUse).map(([key, value]) => ({
        user_id: key,
        name: value,
      }));
      setUserAttendanceData(formatted);

      // Auto-select first user for optimization (if no user is selected)
      if (formatted.length > 0 && !user) {
        setUser(formatted[0].user_id);
      }
    }
  }, [usersData, fallbackUsersData, user]);

  // Records with device configuration
  const {
    data: recordsData,
    refetch,
    isLoading: isLoadingRecords,
  } = useFetchObjectsWithConfig(
    ["attendance-records", queryString, JSON.stringify(deviceConfig)],
    `attendance-config/get_records_with_config/?${queryString}`,
    token,
    deviceConfig
  );

  // Fallback to default records endpoint if custom config fails
  const { data: fallbackRecordsData, refetch: refetchFallbackRecords } =
    useFetchObjects(
      ["attendance-records-fallback", queryString],
      `attendance-records/?${queryString}`,
      token
    );

  useEffect(() => {
    // Use custom config data if available, otherwise fallback to default
    const dataToUse = recordsData || fallbackRecordsData;

    if (dataToUse) {
      const list = dataToUse.results || [];
      setRowCount(dataToUse.total ?? list.length);
      setMeta(dataToUse.meta || null);

      const mapped = list.map((record, index) => ({
        id: page * pageSize + index + 1, // stable id per page
        number: page * pageSize + index + 1,
        user: record.user_name,
        date: record.date,
        time: record.time,
        attendanceType: record.attendanceType,
        status: calculateAttendanceStatus(record.time, record.date), // Use calculated status based on timing rules
      }));
      setRows(mapped);
    }
  }, [recordsData, fallbackRecordsData, page, pageSize]);

  // Helpers for actions
  const handleFilter = () => {
    setPage(0);
    setTimeout(() => {
      refetch();
      refetchFallbackRecords();
    }, 0);
  };

  const handleReset = () => {
    setUser("");
    setYear(currentYear); // Reset to current year
    setMonth(new Date().getMonth() + 1); // Reset to current month
    setStartDate("");
    setEndDate("");
    setForceFresh(false);
    setPage(0);
    setPageSize(50);
    setTimeout(() => {
      refetch();
      refetchFallbackRecords();
    }, 0);
  };

  const handleRefreshFromDevice = () => {
    setForceFresh(true);
    setTimeout(() => {
      refetch();
      refetchFallbackRecords();
      setForceFresh(false);
    }, 0);
  };

  // Build base params for export (use current filters; no page/page_size here)
  const buildExportParams = () => {
    const p = new URLSearchParams();
    if (user) p.append("user_id", user);
    if (year && year !== "") p.append("year", year);
    if (month && month !== "") p.append("month", month);
    if (startDate) p.append("start_date", startDate);
    if (endDate) p.append("end_date", endDate);

    // Use fast subset only if no specific filters are applied
    if (!user && !startDate && !endDate) {
      p.append("last_days", String(DEFAULT_LAST_DAYS));
      p.append("limit_users", String(DEFAULT_LIMIT_USERS));
    }
    return p;
  };

  // Fetch ALL pages for current filters for exporting
  const fetchAllFilteredRecords = async () => {
    setExporting(true);
    try {
      const baseParams = buildExportParams();
      let pageNum = 1;
      let total = Infinity;
      const all = [];

      while (all.length < total) {
        const params = new URLSearchParams(baseParams);
        params.append("page", String(pageNum));
        params.append("page_size", String(EXPORT_PAGE_SIZE));

        const resp = await fetch(
          `${
            process.env.REACT_APP_API_URL
          }/attendance-records/?${params.toString()}`,
          {
            headers: {
              ...(token ? { Authorization: `Token ${token}` } : {}),
            },
          }
        );
        if (!resp.ok) throw new Error(`Export fetch failed (page ${pageNum})`);
        const json = await resp.json();
        const results = json.results || [];
        total = json.total ?? results.length;

        all.push(...results);
        if (results.length === 0) break; // safety
        pageNum += 1;
      }
      return all;
    } finally {
      setExporting(false);
    }
  };

  const exportToExcel = async () => {
    const all = await fetchAllFilteredRecords();

    const wb = XLSX.utils.book_new();
    const title = "Attendance Report";
    const headers = [
      ["#", "User", "Date", "Time", "Attendance Type", "Status"],
    ];

    const rowsAoA = all.map((r, idx) => [
      idx + 1,
      r.user_name,
      r.date,
      r.time,
      r.attendanceType,
      calculateAttendanceStatus(r.time, r.date), // Use calculated status
    ]);

    const ws = XLSX.utils.aoa_to_sheet([[title], ...headers, ...rowsAoA]);
    ws["!merges"] = [
      { s: { r: 0, c: 0 }, e: { r: 0, c: headers[0].length - 1 } },
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Attendance Report");
    XLSX.writeFile(wb, "Attendance_Report.xlsx");
  };

  const exportToPDF = async () => {
    const all = await fetchAllFilteredRecords();

    const doc = new jsPDF();
    doc.autoTable({
      head: [["#", "User", "Date", "Time", "Attendance Type", "Status"]],
      body: all.map((r, idx) => [
        idx + 1,
        r.user_name,
        r.date,
        r.time,
        r.attendanceType,
        calculateAttendanceStatus(r.time, r.date), // Use calculated status
      ]),
      startY: 20,
      styles: { fontSize: 9 },
    });
    doc.save("attendance.pdf");
  };

  // DataGrid columns
  const columns = [
    { field: "number", headerName: "#", width: 80 },
    { field: "user", headerName: t("hr.user"), flex: 1, minWidth: 160 },
    { field: "date", headerName: t("hr.date"), width: 140 },
    { field: "time", headerName: t("hr.time"), width: 140 },
    { field: "attendanceType", headerName: t("hr.attendanceType"), width: 160 },
    { field: "status", headerName: t("hr.status"), width: 140 },
  ];

  const showingFastSubset = !user && !year && !month && !startDate && !endDate;

  return (
    <Container>
      <Paper sx={{ padding: "1rem", marginTop: "2rem" }}>
        <Typography variant="h4" color="primary" fontWeight={500}>
          {t("hr.attendanceReport")}
        </Typography>

        <Grid container spacing={3} sx={{ marginTop: "1rem" }}>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              label={t("hr.selectUser")}
              fullWidth
              value={user}
              onChange={(e) => setUser(e.target.value)}
              helperText={
                !user ? "💡 Select a user for optimized performance" : ""
              }
            >
              <MenuItem value="">{t("hr.allUsers")}</MenuItem>
              {userAttendanceData.map((item) => (
                <MenuItem key={item.user_id} value={item.user_id}>
                  {item.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              select
              label={t("hr.year")}
              fullWidth
              value={year}
              onChange={(e) => setYear(e.target.value)}
            >
              {Years.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.value}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={4}>
            <TextField
              select
              label={t("hr.month")}
              fullWidth
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            >
              {MonthOptions.map((o) => (
                <MenuItem key={o.id} value={o.id}>
                  {o.value}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Optional date range */}
          <Grid item xs={12} sm={6}>
            <TextField
              type="date"
              fullWidth
              label={t("hr.startDate") || "Start date"}
              InputLabelProps={{ shrink: true }}
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              type="date"
              fullWidth
              label={t("hr.endDate") || "End date"}
              InputLabelProps={{ shrink: true }}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </Grid>

          <Grid
            item
            xs={12}
            sm={12}
            display="flex"
            justifyContent="flex-end"
            gap={1}
          >
            <Button variant="outlined" onClick={handleReset}>
              {t("hr.reset") || "Reset"}
            </Button>
            <Button variant="outlined" onClick={handleRefreshFromDevice}>
              {t("hr.refreshFromDevice") || "Force refresh"}
            </Button>
            <Button variant="contained" color="primary" onClick={handleFilter}>
              {t("hr.filter")}
            </Button>
          </Grid>
        </Grid>

        {showingFastSubset && (
          <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
            {t("hr.showingRecentSubset") ||
              `Showing the last ${DEFAULT_LAST_DAYS} days for up to ${DEFAULT_LIMIT_USERS} most recent users.`}
          </Typography>
        )}

        {/* Show optimized filter info */}
        {user && year && month && (
          <Typography variant="body2" sx={{ mt: 1 }} color="primary.main">
            📊 Showing attendance for{" "}
            {userAttendanceData.find((u) => u.user_id === user)?.name ||
              "Selected User"}
            for{" "}
            {new Date(year, month - 1).toLocaleDateString("en-US", {
              month: "long",
              year: "numeric",
            })}
          </Typography>
        )}

        {/* API Status Indicator */}
        {recordsData && !recordsData.error && (
          <Typography
            variant="caption"
            sx={{ mt: 1, display: "block" }}
            color="success.main"
          >
            ✅ Connected to device: {deviceConfig.ip}:{deviceConfig.port}
          </Typography>
        )}
        {fallbackRecordsData && !recordsData && (
          <Typography
            variant="caption"
            sx={{ mt: 1, display: "block" }}
            color="warning.main"
          >
            ⚠️ Using fallback connection (default device settings)
          </Typography>
        )}

        {/* Device connection error */}
        {recordsData?.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Device Connection Error:
            </Typography>
            <Typography variant="body2">{recordsData.error}</Typography>
            {recordsData.device_ip && (
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Device: {recordsData.device_ip}:{recordsData.device_port}
              </Typography>
            )}
            <Typography variant="body2" sx={{ mt: 1 }}>
              💡 Try updating the device configuration above or check if the
              device is powered on.
            </Typography>
          </Alert>
        )}

        {/* Keep table visible; show slim progress while fetching */}
        {isLoadingUsers && (
          <Box display="flex" justifyContent="center" mt={2}>
            <CircularProgress />
          </Box>
        )}
        {isLoadingRecords && <LinearProgress sx={{ mt: 2 }} />}

        {/* Meta info from server cache */}
        {meta && (
          <Typography
            variant="caption"
            sx={{ display: "block", mt: 1 }}
            color="text.secondary"
          >
            Last synced: {meta.generated_at || "n/a"}{" "}
            {meta.source ? `(${meta.source})` : ""}
          </Typography>
        )}

        <Box sx={{ height: 600, width: "100%", mt: 2 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            density="compact"
            disableRowSelectionOnClick
            loading={isLoadingRecords}
            // --- FIX: fully controlled server pagination ---
            paginationMode="server"
            rowCount={rowCount}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={({ page: p, pageSize: ps }) => {
              setPage(p);
              setPageSize(ps);
              setTimeout(() => refetch(), 0);
            }}
            pageSizeOptions={[25, 50, 100, 200]}
            getRowId={(row) => row.id}
          />
        </Box>

        <Box display="flex" justifyContent="flex-end" gap={1} mt={2}>
          <Button
            onClick={exportToExcel}
            variant="contained"
            disabled={exporting}
          >
            {exporting
              ? t("hr.exporting") || "Exporting…"
              : t("hr.exportToExcel")}
          </Button>
          <Button
            onClick={exportToPDF}
            variant="contained"
            color="secondary"
            disabled={exporting}
          >
            {exporting
              ? t("hr.exporting") || "Exporting…"
              : t("hr.exportToPDF")}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default AttendanceReport2;
